import { getProspectDisplayName } from 'helpers/listViewProspectDisplay';
import { Prospect } from 'types/prospect';

type ReferralProspectsApiRow = {
  prospectId?: string | null;
  opportunityId?: string | null;
  leadId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  leadSource?: string | null;
  leadStatus?: string | null;
  opportunityStageName?: string | null;
  opportunityCreatedDate?: string | null;
  leadCreatedDate?: string | null;
  referredByName?: string | null;
};

const emptyProspectDefaults: Omit<
  Prospect,
  | 'prospectId'
  | 'opportunityId'
  | 'leadId'
  | 'customerFirstName'
  | 'customerLastName'
  | 'customerEmail'
  | 'leadSource'
  | 'leadStatus'
  | 'leadStage'
  | 'createdDate'
  | 'referredBy'
> = {
  lastModifiedBy: '',
  createdBy: '',
  prospectQualified: false,
  hasProposals: false,
  signature: '',
  expires: 0,
  channel: '',
  channelLast: '',
  customerCity: '',
  customerState: '',
  customerStreet: '',
  customerZipCode: '',
  lastModifiedDate: '',
  latitude: 0,
  longitude: 0,
  leadType: '',
  usageOption: '',
  utilityCompany: '',
};

const mapReferralProspectsToProspects = (
  rows: ReferralProspectsApiRow[],
): Prospect[] =>
  rows.map((row) => ({
    ...emptyProspectDefaults,
    prospectId: row.prospectId || '',
    opportunityId: row.opportunityId ?? undefined,
    leadId: row.leadId ?? undefined,
    customerFirstName: row.firstName || '',
    customerLastName: row.lastName || '',
    customerEmail: row.email || '',
    leadSource: row.leadSource || 'Customer Referral',
    leadStatus: row.leadStatus || '',
    leadStage: row.opportunityStageName || '',
    createdDate: row.opportunityCreatedDate || row.leadCreatedDate || '',
    referredBy: row.referredByName || '',
  }));

type ReferralSortField = 'name' | 'created' | 'referredBy' | 'status';
type ReferralSortDirection = 'ASC' | 'DESC';

const getReferralProspectName = (prospect: Prospect) =>
  getProspectDisplayName(prospect);

const getReferralProspectStatus = (prospect: Prospect) =>
  prospect.leadStatus || prospect.leadStage || '';

/** leadStage is mapped from API opportunityStageName */
const getReferralStatusDisplay = (prospect: Prospect) => {
  const opportunityStageName = (prospect.leadStage || '').trim();
  if (opportunityStageName) {
    return `Opportunity: ${opportunityStageName}`;
  }
  const leadStatus = (prospect.leadStatus || '').trim();
  return `Lead: ${leadStatus || '-'}`;
};

const getReferralMobileStatusPill = (prospect: Prospect) =>
  getReferralStatusDisplay(prospect);

const getReferralSortValue = (
  prospect: Prospect,
  field: ReferralSortField,
): string | number => {
  switch (field) {
    case 'name':
      return getReferralProspectName(prospect).toLowerCase();
    case 'created':
      return Date.parse(prospect.createdDate || '') || 0;
    case 'referredBy':
      return (prospect.referredBy || '').toLowerCase();
    case 'status':
      return getReferralProspectStatus(prospect).toLowerCase();
    default:
      return '';
  }
};

const isReferralOpportunityProspect = (prospect: Prospect) =>
  Boolean((prospect.leadStage || '').trim());

const getReferralStatusSortLabel = (prospect: Prospect) => {
  if (isReferralOpportunityProspect(prospect)) {
    return (prospect.leadStage || '').trim().toLowerCase();
  }
  return (prospect.leadStatus || '').trim().toLowerCase();
};

const sortReferralProspects = (
  prospects: Prospect[],
  field: ReferralSortField,
  direction: ReferralSortDirection,
): Prospect[] => {
  return [...prospects].sort((a, b) => {
    if (field === 'status') {
      const tierA = isReferralOpportunityProspect(a) ? 1 : 0;
      const tierB = isReferralOpportunityProspect(b) ? 1 : 0;

      if (tierA !== tierB) {
        return tierA - tierB;
      }

      const labelCompare = getReferralStatusSortLabel(a).localeCompare(
        getReferralStatusSortLabel(b),
      );
      return direction === 'ASC' ? labelCompare : -labelCompare;
    }

    const valueA = getReferralSortValue(a, field);
    const valueB = getReferralSortValue(b, field);

    const result =
      typeof valueA === 'number' && typeof valueB === 'number'
        ? valueA - valueB
        : String(valueA).localeCompare(String(valueB));

    return direction === 'ASC' ? result : -result;
  });
};

export {
  getReferralMobileStatusPill,
  getReferralProspectName,
  getReferralProspectStatus,
  mapReferralProspectsToProspects,
  sortReferralProspects,
};
export type {
  ReferralProspectsApiRow,
  ReferralSortField,
  ReferralSortDirection,
};
